import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  FormContainer,
  FormHeader,
  FormField,
  SubmitButton,
  Message,
  ValidationError,
} from "./VincularConsultaForm.styles";
import api from "../../services/api";
import { PROJECT_NAME } from "../../constants/project";

// Converte data "dd/mm/yyyy" (retornada pela API) para "yyyy-mm-dd" (input date).
const toInputDate = (dataFim) => {
  if (!dataFim || typeof dataFim !== "string") return undefined;
  const [day, month, year] = dataFim.split('/');
  if (!day || !month || !year) return undefined;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const VincularConsultaForm = ({ gestanteCpf, gravidezId, onClose, onConsultaVinculada }) => {
  const [gestacoes, setGestacoes] = useState([]);
  const [formValues, setFormValues] = useState({
    gravidez_id: gravidezId ? String(gravidezId) : "",
    data_consulta: "",
    profissional: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Buscar gestações vinculadas à gestante
  useEffect(() => {
    const fetchGestacoes = async () => {
      try {
        const { data } = await api.get(`/gestante/${gestanteCpf}/consultas`);
        setGestacoes(data);
      } catch (error) {
        console.error("Erro ao buscar gestações:", error);
        setGestacoes([]);
      }
    };

    if (gestanteCpf) {
      fetchGestacoes();
    }
  }, [gestanteCpf]);

  // Retorna a data de encerramento (dd/mm/yyyy) da gestação selecionada, se houver.
  const getDataFim = (gravidezIdValue) => {
    const gestacao = gestacoes.find((g) => String(g.id) === String(gravidezIdValue));
    return gestacao?.data_fim || null;
  };

  const validateDataConsulta = (data, dataFim) => {
    if (!data) return "";

    const dataConsulta = new Date(data);
    const dataMinima = new Date("2025-01-01");

    if (dataConsulta < dataMinima) {
      return `Não é permitido adicionar consulta com data anterior a 01/01/2025, que é a data inicial do programa ${PROJECT_NAME}.`;
    }

    const dataFimInput = toInputDate(dataFim);
    if (dataFimInput && dataConsulta > new Date(dataFimInput)) {
      return `Não é permitido registrar consulta com data posterior à data de encerramento da gestação (${dataFim}).`;
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    // Validação em tempo real para data_consulta
    if (name === "data_consulta") {
      const error = validateDataConsulta(value, getDataFim(formValues.gravidez_id));
      setValidationErrors((prev) => ({
        ...prev,
        data_consulta: error,
      }));
    }

    // Ao trocar a gestação, revalida a data já preenchida contra o novo encerramento.
    if (name === "gravidez_id") {
      const error = validateDataConsulta(formValues.data_consulta, getDataFim(value));
      setValidationErrors((prev) => ({
        ...prev,
        data_consulta: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");
    setValidationErrors({});

    // Validar data da consulta antes de enviar
    const dataError = validateDataConsulta(formValues.data_consulta, getDataFim(formValues.gravidez_id));
    if (dataError) {
      setValidationErrors({ data_consulta: dataError });
      setIsSubmitting(false);
      return;
    }

    const sanitizedCpf = gestanteCpf.replace(/[.-]/g, "");
    const formattedDate = new Date(formValues.data_consulta).toISOString();

    const payload = {
      gestante_cpf: sanitizedCpf,
      gravidez_id: formValues.gravidez_id,
      data_consulta: formattedDate,
      profissional: formValues.profissional,
    };

    try {
      const { status } = await api.post("/gestante/gravidez/consulta", payload);

      if (status === 201) {
        setSuccessMessage("Consulta vinculada com sucesso!");
        setFormValues({ gravidez_id: "", data_consulta: "", profissional: "" });

        if (onConsultaVinculada) onConsultaVinculada();

        setTimeout(onClose, 1500);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Erro ao vincular consulta.";
      setErrorMessage(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <FormHeader>Vincular Consulta</FormHeader>
      <form onSubmit={handleSubmit}>
        <FormField>
          <label htmlFor="gravidez_id">Gestação:</label>
          <select
            id="gravidez_id"
            name="gravidez_id"
            value={formValues.gravidez_id}
            onChange={handleChange}
            required
          >
            <option value="">Selecione...</option>
            {gestacoes.map((g) => (
              <option key={g.id} value={g.id}>
                {g.data} - {g.status_gravidez}
              </option>
            ))}
          </select>
          {gestacoes.length === 0 && (
            <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>
              Não há gestações para vincular consultas.
            </p>
          )}
          {validationErrors.gravidez_id && (
            <ValidationError>{validationErrors.gravidez_id}</ValidationError>
          )}
        </FormField>

        <FormField>
          <label htmlFor="data_consulta">Data da Consulta:</label>
          <input
            type="date"
            id="data_consulta"
            name="data_consulta"
            value={formValues.data_consulta}
            onChange={handleChange}
            max={toInputDate(getDataFim(formValues.gravidez_id))}
            required
          />
          {validationErrors.data_consulta && (
            <ValidationError>{validationErrors.data_consulta}</ValidationError>
          )}
        </FormField>

        <FormField>
          <label htmlFor="profissional">Profissional:</label>
          <input
            type="text"
            id="profissional"
            name="profissional"
            value={formValues.profissional}
            onChange={handleChange}
            placeholder="Digite o nome do profissional"
            required
          />
          {validationErrors.profissional && (
            <ValidationError>{validationErrors.profissional}</ValidationError>
          )}
        </FormField>

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Vinculando..." : "Vincular Consulta"}
        </SubmitButton>
      </form>

      {successMessage && <Message>{successMessage}</Message>}
      {errorMessage && <Message isError>{errorMessage}</Message>}
    </FormContainer>
  );
};

VincularConsultaForm.propTypes = {
  gestanteCpf: PropTypes.string.isRequired,
  gravidezId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onConsultaVinculada: PropTypes.func,
};

export default VincularConsultaForm;
